import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaEye, FaFileInvoiceDollar, FaUser, FaProjectDiagram } from 'react-icons/fa';
import { Offer } from '../services/OfferService';
import { Project } from '../../contacts/services/ProjectService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';

interface OfferHeaderCardProps {
  isEdit: boolean;
  offer: Offer | null;
  partner: BusinessPartner | null;
  project: Project | null;
  saving: boolean;
  onSaveDraft: () => void;
  onSaveAndPreview: () => void;
  onBack: () => void;
}

export default function OfferHeaderCard({
  isEdit,
  offer,
  partner,
  project,
  saving,
  onSaveDraft,
  onSaveAndPreview,
  onBack,
}: OfferHeaderCardProps): React.JSX.Element {
  return (
    <>
      {/* Back Button */}
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          marginBottom: '24px',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: 600,
          color: '#333',
          cursor: 'pointer',
        }}
      >
        <FaArrowLeft size={14} />
        Back
      </button>

      {/* Header Card */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: '12px',
          padding: '32px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', gap: '24px', flex: 1 }}>
            {/* Avatar */}
            <div
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: '#e0e3ef',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FaFileInvoiceDollar size={40} color="#021a4a" />
            </div>

            {/* Info */}
            <div style={{ flex: 1 }}>
              {/* Title - Show offer name when editing, "New offer" when creating */}
              <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0f2b5a', margin: '0 0 12px 0' }}>
                {isEdit && offer ? (offer.title || 'Untitled Offer') : 'New offer'}
              </h1>

              {/* Offer Number */}
              {offer && (
                <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      <strong>No.:</strong> {offer.offer_number}
                    </span>
                  </div>
                </div>
              )}

              {/* Customer & Project Row */}
              <div style={{ display: 'flex', gap: '24px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {partner && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaUser color="#666" size={14} />
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      <Link
                        to={`/contacts/${partner.id}`}
                        style={{ color: '#021a4a', textDecoration: 'none' }}
                      >
                        {BusinessPartnerService.getDisplayName(partner)}
                      </Link>
                    </span>
                  </div>
                )}
                {project && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaProjectDiagram color="#666" size={14} />
                    <span style={{ fontSize: '14px', color: '#333' }}>
                      <Link
                        to={`/projects/${project.id}`}
                        style={{ color: '#021a4a', textDecoration: 'none' }}
                      >
                        {project.name}
                      </Link>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
            <button
              onClick={onSaveDraft}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                background: '#fff',
                color: '#021a4a',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              <FaSave size={14} />
              Save draft
            </button>
            <button
              onClick={onSaveAndPreview}
              disabled={saving}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 20px',
                border: 'none',
                borderRadius: '6px',
                background: '#021a4a',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              <FaEye size={14} />
              Save & preview
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
